import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Package, Pencil, Trash2, Sparkles, Loader2, ChevronRight, ArrowRightLeft } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ListSearch, { filterBy } from "@/components/ListSearch";
import { toast } from "sonner";

const TYPE_LABEL = {
    sales: "Sell",
    purchase: "Buy",
    both: "Sell & Buy",
};

const TYPE_STYLES = {
    sales: "bg-secondary/10 text-secondary-dark",
    purchase: "bg-coral/10 text-coral-dark",
    both: "bg-ocean-50 text-ocean-700",
};

const blank = {
    name: "",
    description: "",
    item_type: "sales",
    rate: 0,
    tax_rate: 0,
    account_id: "",
    active: true,
};

export default function Items() {
    const { currentOrg } = useAuth();
    const [items, setItems] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // item being edited
    const [form, setForm] = useState(blank);
    const [q, setQ] = useState("");
    const [tidyOpen, setTidyOpen] = useState(false);
    const [tidyLoading, setTidyLoading] = useState(false);
    const [tidyGroups, setTidyGroups] = useState([]);
    // Map<groupId, {canonicalId, selectedDuplicates:Set<id>}> — per-group selection state
    const [tidySelections, setTidySelections] = useState({});
    const [tidyMerging, setTidyMerging] = useState(false);

    const load = async () => {
        const [{ data: it }, { data: acc }] = await Promise.all([
            api.get("/items"),
            api.get("/accounts"),
        ]);
        setItems(it.items || []);
        setAccounts(acc.items || []);
    };

    useEffect(() => {
        if (currentOrg) load();
    }, [currentOrg]);

    const relevantAccounts = useMemo(() => {
        const type = form.item_type;
        if (type === "purchase") return accounts.filter((a) => a.type === "expense");
        if (type === "sales") return accounts.filter((a) => a.type === "revenue");
        // both
        return accounts.filter((a) => a.type === "revenue" || a.type === "expense");
    }, [accounts, form.item_type]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...blank, tax_rate: currentOrg?.cayman_no_tax_mode ? 0 : (currentOrg?.default_tax_rate || 0) });
        setOpen(true);
    };

    const openEdit = (it) => {
        setEditing(it);
        setForm({
            name: it.name,
            description: it.description || "",
            item_type: it.item_type,
            rate: it.rate,
            tax_rate: it.tax_rate,
            account_id: it.account_id || "",
            active: it.active,
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.name.trim()) return toast.error("Name is required");
        const payload = {
            ...form,
            rate: Number(form.rate || 0),
            tax_rate: Number(form.tax_rate || 0),
            account_id: form.account_id || null,
        };
        try {
            if (editing) {
                await api.put(`/items/${editing.id}`, payload);
                toast.success("Item updated");
            } else {
                await api.post("/items", payload);
                toast.success("Item created");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (it) => {
        if (!window.confirm(`Delete "${it.name}"?`)) return;
        try {
            await api.delete(`/items/${it.id}`);
            toast.success("Item deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const accountName = (id) => accounts.find((a) => a.id === id)?.name || "—";
    const accountCode = (id) => accounts.find((a) => a.id === id)?.code || "";

    const openTidyUp = async () => {
        setTidyOpen(true);
        setTidyGroups([]);
        setTidySelections({});
        setTidyLoading(true);
        try {
            const { data } = await api.get("/items/duplicate-groups");
            const groups = data.groups || [];
            setTidyGroups(groups);
            // Preselect suggested canonical + all other members as duplicates-to-merge.
            const init = {};
            for (const g of groups) {
                const dupIds = new Set(g.members.map((m) => m.id).filter((id) => id !== g.suggested_canonical_id));
                init[g.id] = { canonicalId: g.suggested_canonical_id, selectedDuplicates: dupIds };
            }
            setTidySelections(init);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setTidyLoading(false);
        }
    };

    const setCanonical = (groupId, itemId) => {
        setTidySelections((prev) => {
            const g = tidyGroups.find((x) => x.id === groupId);
            const others = new Set(g.members.map((m) => m.id).filter((id) => id !== itemId));
            // Preserve already-unchecked entries when the canonical changes.
            const prevSelected = prev[groupId]?.selectedDuplicates || new Set();
            const merged = new Set();
            for (const m of g.members) {
                if (m.id === itemId) continue;
                if (prevSelected.has(m.id) || !prev[groupId]) merged.add(m.id);
            }
            return { ...prev, [groupId]: { canonicalId: itemId, selectedDuplicates: others.size ? merged : new Set() } };
        });
    };

    const toggleDup = (groupId, itemId) => {
        setTidySelections((prev) => {
            const cur = prev[groupId] || { canonicalId: null, selectedDuplicates: new Set() };
            const next = new Set(cur.selectedDuplicates);
            if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
            return { ...prev, [groupId]: { ...cur, selectedDuplicates: next } };
        });
    };

    const totalMergeCount = useMemo(() => (
        Object.values(tidySelections).reduce((acc, s) => acc + (s.selectedDuplicates?.size || 0), 0)
    ), [tidySelections]);

    const runMerge = async () => {
        const calls = [];
        for (const g of tidyGroups) {
            const sel = tidySelections[g.id];
            if (!sel || !sel.canonicalId || sel.selectedDuplicates.size === 0) continue;
            calls.push(api.post("/items/merge", {
                canonical_id: sel.canonicalId,
                duplicate_ids: Array.from(sel.selectedDuplicates),
            }));
        }
        if (calls.length === 0) {
            toast.error("No duplicates selected to merge");
            return;
        }
        setTidyMerging(true);
        try {
            const results = await Promise.allSettled(calls);
            const ok = results.filter((r) => r.status === "fulfilled");
            const failed = results.length - ok.length;
            const removed = ok.reduce((acc, r) => acc + (r.value?.data?.duplicates_removed || 0), 0);
            const inv = ok.reduce((acc, r) => acc + (r.value?.data?.invoice_docs_touched || 0), 0);
            const bill = ok.reduce((acc, r) => acc + (r.value?.data?.bill_docs_touched || 0), 0);
            const parts = [];
            if (removed) parts.push(`${removed} duplicate${removed === 1 ? "" : "s"} merged`);
            if (inv) parts.push(`${inv} invoice${inv === 1 ? "" : "s"} updated`);
            if (bill) parts.push(`${bill} bill${bill === 1 ? "" : "s"} updated`);
            toast.success(parts.join(" · ") || "Merge complete");
            if (failed) toast.error(`${failed} group${failed === 1 ? "" : "s"} failed to merge`);
            setTidyOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setTidyMerging(false);
        }
    };

    return (
        <div>
            <PageHeader
                title="Items"
                description="Build a catalog of products and services you regularly sell or buy. Link each to a Chart of Account so every invoice and bill posts to the right GL line."
                action={
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={openTidyUp}
                            className="rounded-xl border-secondary/40 text-secondary-dark hover:bg-secondary/10"
                            data-testid="items-tidy-up-btn"
                            title="Find and merge duplicate items"
                        >
                            <Sparkles className="h-4 w-4 mr-1.5" /> Tidy up
                        </Button>
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button onClick={openCreate} className="bg-ocean-700 text-white rounded-xl" data-testid="new-item-btn">
                                    <Plus className="h-4 w-4 mr-1.5" /> New item
                                </Button>
                            </DialogTrigger>
                        <DialogContent className="sm:max-w-xl">
                            <DialogHeader>
                                <DialogTitle>{editing ? "Edit item" : "New item"}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-2">
                                <div>
                                    <Label>Name</Label>
                                    <Input
                                        className="mt-1.5"
                                        placeholder="e.g. Consulting hour"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        data-testid="item-name"
                                    />
                                </div>
                                <div>
                                    <Label>Description</Label>
                                    <Input
                                        className="mt-1.5"
                                        placeholder="Optional — shown on invoices"
                                        value={form.description}
                                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Type</Label>
                                        <Select
                                            value={form.item_type}
                                            onValueChange={(v) => setForm({ ...form, item_type: v, account_id: "" })}
                                        >
                                            <SelectTrigger className="mt-1.5" data-testid="item-type">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="sales">Sell (goods/services you sell)</SelectItem>
                                                <SelectItem value="purchase">Buy (things you purchase)</SelectItem>
                                                <SelectItem value="both">Both</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Default rate ({currentOrg?.currency || "KYD"})</Label>
                                        <Input
                                            className="mt-1.5"
                                            type="number"
                                            value={form.rate}
                                            onChange={(e) => setForm({ ...form, rate: e.target.value })}
                                            data-testid="item-rate"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Chart of Account</Label>
                                        <Select
                                            value={form.account_id || "__none__"}
                                            onValueChange={(v) => setForm({ ...form, account_id: v === "__none__" ? "" : v })}
                                        >
                                            <SelectTrigger className="mt-1.5" data-testid="item-account">
                                                <SelectValue placeholder="Pick account" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none__">— None —</SelectItem>
                                                {relevantAccounts.map((a) => (
                                                    <SelectItem key={a.id} value={a.id}>
                                                        {a.code} · {a.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <div className="text-[11px] text-muted-foreground mt-1">
                                            {form.item_type === "sales"
                                                ? "Typically a Revenue account"
                                                : form.item_type === "purchase"
                                                ? "Typically an Expense account"
                                                : "Revenue or Expense"}
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Default tax %</Label>
                                        <Input
                                            className="mt-1.5"
                                            type="number"
                                            value={form.tax_rate}
                                            disabled={currentOrg?.cayman_no_tax_mode}
                                            onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                                        />
                                        {currentOrg?.cayman_no_tax_mode && (
                                            <div className="text-[11px] text-muted-foreground mt-1">
                                                Cayman no-tax mode is on.
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <DialogFooter className="pt-2">
                                    <Button variant="outline" onClick={() => setOpen(false)}>
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={submit}
                                        className="bg-ocean-700 text-white"
                                        data-testid="submit-item-btn"
                                    >
                                        {editing ? "Save" : "Create"}
                                    </Button>
                                </DialogFooter>
                            </div>
                        </DialogContent>
                    </Dialog>
                    </div>
                }
            />

            {items.length === 0 ? (
                <EmptyState
                    icon={Package}
                    title="No items yet"
                    description="Add items you commonly sell or buy. They'll be one-click reusable in invoices and bills, and automatically post to the right Chart of Account."
                />
            ) : (
                <>
                    <div className="mb-4 flex items-center gap-3">
                        <ListSearch value={q} onChange={setQ} placeholder="Search items by name, description or type…" testId="items-search" />
                        {q && <span className="text-xs text-muted-foreground">{filterBy(items, q, ["name", "description", "item_type"]).length} of {items.length}</span>}
                    </div>
                    <div className="rounded-3xl bg-white dark:bg-slate-900 border border-sand-200 dark:border-slate-800 shadow-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-sand-100 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-5 py-3 font-semibold">Name</th>
                                        <th className="text-left px-5 py-3 font-semibold">Type</th>
                                        <th className="text-right px-5 py-3 font-semibold">Rate</th>
                                        <th className="text-right px-5 py-3 font-semibold">Tax %</th>
                                        <th className="text-left px-5 py-3 font-semibold">Posts to</th>
                                        <th className="px-5 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-sand-200 dark:divide-slate-800">
                                    {filterBy(items, q, ["name", "description", "item_type"]).map((it) => (
                                    <tr key={it.id} className="hover:bg-sand-50/60 dark:hover:bg-slate-800/40 transition-colors" data-testid={`item-row-${it.id}`}>
                                        <td className="px-5 py-3">
                                            <div className="font-medium text-ocean-700 dark:text-slate-100">{it.name}</div>
                                            {it.description && (
                                                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                                    {it.description}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            <Badge className={`${TYPE_STYLES[it.item_type]} border-0`}>
                                                {TYPE_LABEL[it.item_type]}
                                            </Badge>
                                        </td>
                                        <td className="px-5 py-3 text-right font-mono">
                                            {fmtMoney(it.rate, currentOrg?.currency || "KYD")}
                                        </td>
                                        <td className="px-5 py-3 text-right font-mono text-muted-foreground">
                                            {it.tax_rate || 0}%
                                        </td>
                                        <td className="px-5 py-3">
                                            {it.account_id ? (
                                                <div>
                                                    <div className="font-mono text-xs text-muted-foreground">
                                                        {accountCode(it.account_id)}
                                                    </div>
                                                    <div className="text-sm">{accountName(it.account_id)}</div>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">— Not linked —</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-right whitespace-nowrap">
                                            <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`}>
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => remove(it)} data-testid={`delete-item-${it.id}`}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            <Dialog open={tidyOpen} onOpenChange={(v) => { if (!tidyMerging) setTidyOpen(v); }}>
                <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-secondary" /> Tidy up items
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2" data-testid="tidy-up-body">
                        {tidyLoading && (
                            <div className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-3">
                                <Loader2 className="h-6 w-6 animate-spin text-secondary" />
                                Scanning your catalog for duplicates…
                            </div>
                        )}
                        {!tidyLoading && tidyGroups.length === 0 && (
                            <div className="py-16 text-center" data-testid="tidy-up-empty">
                                <div className="h-14 w-14 rounded-2xl bg-secondary/10 text-secondary grid place-items-center mx-auto">
                                    <Sparkles className="h-7 w-7" />
                                </div>
                                <h3 className="mt-4 font-heading text-xl text-ocean-700">Your catalog is tidy</h3>
                                <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
                                    We scanned {items.length} item{items.length === 1 ? "" : "s"} and couldn't find any likely duplicates. Nicely done.
                                </p>
                            </div>
                        )}
                        {!tidyLoading && tidyGroups.length > 0 && (
                            <div className="space-y-5">
                                <p className="text-sm text-muted-foreground">
                                    We found {tidyGroups.length} possible duplicate group{tidyGroups.length === 1 ? "" : "s"}. Pick the name to keep, uncheck anything that isn't actually a duplicate, then merge. Every invoice and bill line referencing a duplicate is re-pointed to the canonical item — no financial data is lost.
                                </p>
                                {tidyGroups.map((g) => {
                                    const sel = tidySelections[g.id] || { canonicalId: g.suggested_canonical_id, selectedDuplicates: new Set() };
                                    return (
                                        <div key={g.id} className="rounded-2xl border border-sand-300 bg-white overflow-hidden" data-testid={`tidy-group-${g.id}`}>
                                            <div className="px-4 py-3 bg-sand-50 border-b border-sand-200 text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                                                <span>Duplicate group</span>
                                                <span className="text-secondary-dark normal-case font-medium">{g.members.length} candidates</span>
                                            </div>
                                            <div className="divide-y divide-sand-100">
                                                {g.members.map((m) => {
                                                    const isCanonical = sel.canonicalId === m.id;
                                                    const willMerge = !isCanonical && sel.selectedDuplicates.has(m.id);
                                                    return (
                                                        <div
                                                            key={m.id}
                                                            className={`px-4 py-3 flex items-start gap-3 ${isCanonical ? "bg-secondary/5" : willMerge ? "bg-coral/5" : ""}`}
                                                            data-testid={`tidy-row-${m.id}`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name={g.id}
                                                                checked={isCanonical}
                                                                onChange={() => setCanonical(g.id, m.id)}
                                                                className="mt-1 h-4 w-4 accent-secondary cursor-pointer"
                                                                aria-label={`Keep ${m.name}`}
                                                                data-testid={`tidy-keep-${m.id}`}
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <div className="font-medium text-ocean-700 text-sm truncate">{m.name}</div>
                                                                    {isCanonical && (
                                                                        <Badge className="bg-secondary text-white text-[10px] uppercase tracking-wider">Keep</Badge>
                                                                    )}
                                                                    {willMerge && (
                                                                        <Badge className="bg-coral/15 text-coral-dark text-[10px] uppercase tracking-wider">
                                                                            <ArrowRightLeft className="h-3 w-3 mr-1" /> Merge into keep
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                {m.description && (
                                                                    <div className="text-xs text-muted-foreground truncate">{m.description}</div>
                                                                )}
                                                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                                                    {m.item_type === "both" ? "Sales / Purchase" : m.item_type} · {fmtMoney(m.rate, currentOrg?.currency)}
                                                                    {m.tax_rate > 0 && ` · ${m.tax_rate}% tax`}
                                                                    {(m.usage?.invoices || m.usage?.bills) ? (
                                                                        <span className="ml-2 text-secondary-dark">
                                                                            · used on {m.usage.invoices} invoice{m.usage.invoices === 1 ? "" : "s"} + {m.usage.bills} bill{m.usage.bills === 1 ? "" : "s"}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="ml-2">· no usage yet</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {!isCanonical && (
                                                                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer shrink-0 select-none">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={willMerge}
                                                                        onChange={() => toggleDup(g.id, m.id)}
                                                                        className="h-3.5 w-3.5 accent-coral cursor-pointer"
                                                                        data-testid={`tidy-dup-${m.id}`}
                                                                    />
                                                                    Merge
                                                                </label>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <DialogFooter className="flex-shrink-0 flex items-center justify-between gap-3">
                        <div className="text-xs text-muted-foreground hidden sm:block">
                            {totalMergeCount > 0 ? `${totalMergeCount} item${totalMergeCount === 1 ? "" : "s"} will be merged.` : "Nothing selected to merge."}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" onClick={() => setTidyOpen(false)} disabled={tidyMerging} data-testid="tidy-cancel">Cancel</Button>
                            <Button
                                onClick={runMerge}
                                disabled={tidyMerging || totalMergeCount === 0 || tidyGroups.length === 0}
                                className="bg-ocean-700 text-white"
                                data-testid="tidy-merge"
                            >
                                {tidyMerging ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Merging…</>) : (<><ChevronRight className="h-4 w-4 mr-1.5" /> Merge {totalMergeCount} item{totalMergeCount === 1 ? "" : "s"}</>)}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
